import { assert } from '@ember/debug';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { local } from 'embroider-css-modules';

import type { IconName } from '../../types/components/prism/icon.ts';
import styles from './accordion.module.css';
import PrismIcon from './icon.gts';

interface PrismAccordionSignature {
  Args: {
    description?: string;
    icon?: IconName;
    initiallyOpened?: boolean;
    onToggle?: (event: Event) => unknown;
    pseudo?: 'hover' | 'focus';
    title?: string;
  };
  Blocks: {
    default: [];
    summaryAfter: [];
  };
  Element: HTMLDetailsElement;
}

export default class PrismAccordion extends Component<PrismAccordionSignature> {
  @tracked isOpen = this.args.initiallyOpened ?? false;

  get hasDescriptionAndTitle(): boolean {
    return Boolean(this.args.description) && Boolean(this.args.title);
  }

  get hasDescriptionOnly(): boolean {
    assert(
      'Define at least a title or a description',
      this.args.title || this.args.description,
    );

    return Boolean(this.args.description) && !this.args.title;
  }

  @action onToggle(event: Event): void {
    this.isOpen = (event.currentTarget as HTMLDetailsElement).open;

    this.args.onToggle?.(event);
  }

  <template>
    <details
      class={{styles.accordion}}
      data-test-accordion
      open={{this.isOpen}}
      {{on "toggle" this.onToggle}}
      ...attributes
    >
      <summary class={{styles.summary}} data-test-pseudo={{@pseudo}}>
        <div class={{styles.header}}>
          <div class={{styles.header-left}}>
            {{#if @icon}}
              <PrismIcon @name={{@icon}} />
            {{/if}}

            {{#if this.hasDescriptionOnly}}
              <p class={{styles.description}}>
                {{@description}}
              </p>
            {{else}}
              <h4>{{@title}}</h4>
            {{/if}}
          </div>

          <div class={{styles.header-right}}>
            {{#if (has-block "summaryAfter")}}
              {{yield to="summaryAfter"}}
            {{/if}}

            <PrismIcon @name={{if this.isOpen "chevron-up" "chevron-down"}} />
          </div>
        </div>

        {{#if this.hasDescriptionAndTitle}}
          <p class={{local styles "description" (if @icon "is-indented")}}>
            {{@description}}
          </p>
        {{/if}}
      </summary>

      <div class={{styles.content}}>
        {{yield}}
      </div>
    </details>
  </template>
}
